import AvailableTag from "components/common/AvailableTag";
import { getLabelStyle } from "components/common/labels";
import React from "react";
import { CapacityEnergyCost } from "types/busType";
import {
    ComponentConfig,
    isPricedConstraintConfig
} from "types/componentConfigPerRegion";
import { GeneratorType, STORAGE_TYPE } from "types/generatorType";
import {
    CAPACITY_RESOURCE_TYPE,
    CHARGING_RESOURCE_TYPE,
    ENERGY_RESOURCE_TYPE
} from "types/resourceType";

export type BusSizeTags = {
    readonly energyTag: React.ReactNode;
    readonly chargingTag: React.ReactNode;
    readonly capacityTag: React.ReactNode;
};

/**
 * The available-MW pills for a bus, for the panes that render *before* the bus
 * is unlocked (the unlock prompt, and the locked pane when we can't unlock).
 *
 * Available MW isn't gated behind an unlock -- the sidebar lists it for every
 * bus, locked or not, and the map colours buses by it -- so hiding it here would
 * only hide something the user can read one panel to the left.
 *
 * Built deliberately without `onClick`: wiring these up the way the unlocked
 * pane does would offer "Preview project size", whose cost summary is computed
 * from constraint data a locked bus doesn't return.
 */
export const getBusSizeTags = ({
    scopedCapacityEnergyCost,
    componentConfig,
    generator
}: {
    scopedCapacityEnergyCost: CapacityEnergyCost;
    componentConfig: ComponentConfig;
    generator: GeneratorType;
}): BusSizeTags => {
    const labelStyle = getLabelStyle(componentConfig, generator);
    const showCapacity =
        isPricedConstraintConfig(componentConfig) &&
        componentConfig.showCapacity;
    const showCharging =
        generator === STORAGE_TYPE && componentConfig.showChargingSize;

    return {
        energyTag: (
            <AvailableTag
                size={scopedCapacityEnergyCost.energySize}
                resourceType={ENERGY_RESOURCE_TYPE}
                labelStyle={labelStyle}
            />
        ),
        chargingTag: showCharging && (
            <AvailableTag
                size={scopedCapacityEnergyCost.chargingSize}
                resourceType={CHARGING_RESOURCE_TYPE}
                labelStyle={labelStyle}
            />
        ),
        capacityTag: showCapacity && (
            <AvailableTag
                size={scopedCapacityEnergyCost.capacitySize}
                resourceType={CAPACITY_RESOURCE_TYPE}
                labelStyle={labelStyle}
            />
        )
    };
};
